import { useState, useRef, useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import { IconHelp } from '../atoms';
import { getArticle } from '../help/registry';

/**
 * HelpTip — the small "?" beside a heading or a field.
 *
 * Click it and you get two or three plain sentences about the thing you are
 * looking at, plus a link to the full article. It is the shortest path from
 * "what does this mean" to an answer, and it never navigates you away from the
 * work you were doing.
 *
 * THE TEXT COMES FROM THE HELP CORPUS, NOT FROM THE CALL SITE. Passing prose in
 * as a prop is how a tooltip and its help article drift apart until they say
 * different things — so a HelpTip names an article slug, and the summary it
 * shows IS that article's summary. One edit updates both.
 *
 * `text` exists only for the case where an article's summary is the wrong
 * granularity for one specific field (the article is about invoices; the field
 * is about one checkbox on it). Prefer the slug alone.
 */

export interface HelpTipProps {
  /** Slug of the article this explains. Must exist — a bad slug renders nothing. */
  topic: string;
  /** Override the shown text. Use sparingly; see the note above. */
  text?: string;
  /** What the "?" is explaining, for screen readers: "Help with fee basis". */
  label?: string;
  size?: number;
}

export function HelpTip({ topic, text, label, size = 14 }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();
  const article = getArticle(topic);

  // Close on an outside click or Escape. Without this the panel stays open
  // behind whatever the user clicks next, which reads as a stuck overlay.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // An unknown slug renders NOTHING rather than an empty popover. A "?" that
  // opens to blank space is worse than no "?" at all — and registry.test.ts
  // fails the build on a slug that does not resolve, so this is a runtime
  // backstop, not the primary defence.
  if (!article) return null;

  const body = text ?? article.summary;
  const accessibleLabel = label ?? `Help with ${article.title}`;

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={accessibleLabel}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size + 8,
          height: size + 8,
          padding: 0,
          marginLeft: 4,
          border: 'none',
          borderRadius: '50%',
          background: 'transparent',
          color: open ? 'var(--brand-primary)' : 'var(--color-ink-3)',
          cursor: 'pointer',
          lineHeight: 0,
        }}
      >
        <IconHelp size={size} />
      </button>

      {open && (
        <span
          id={panelId}
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 60,
            width: 300,
            padding: 'var(--sp-4)',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--sh-lg)',
            textAlign: 'left',
            // The panel is absolutely positioned inside an inline-flex span, so
            // it must re-establish normal text flow for its own content.
            display: 'block',
            whiteSpace: 'normal',
          }}
        >
          <span style={{
            display: 'block',
            fontWeight: 'var(--fw-semibold)',
            fontSize: 'var(--fs-md)',
            color: 'var(--color-ink)',
            marginBottom: 4,
          }}>{article.title}</span>

          <span style={{
            display: 'block',
            fontSize: 'var(--fs-md)',
            lineHeight: 'var(--lh-relaxed)',
            color: 'var(--color-ink-2)',
          }}>{body}</span>

          <Link
            to={`/help/${article.slug}`}
            onClick={() => setOpen(false)}
            style={{
              display: 'inline-block',
              marginTop: 'var(--sp-3)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-medium)',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
            }}
          >Read the full guide →</Link>
        </span>
      )}
    </span>
  );
}
